import React, { useEffect } from 'react';
import { Translation, getI18n } from 'react-i18next';
import { Button, Table } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router';

import {
  getColumnSearchProps,
  getColumnDateRangeProps,
} from '../../../elements/components/TableColumnFilters';
import { pathTo } from '../../../Routes';
import Format from '../../../../../lib/Format';
import QueryString from '../../../../../lib/QueryString';
import Logger from '../../../../../lib/Logger';

const RegistrationsList = ({
  component,
  page,
  limit,
  order,
  total,
  filter,
  load,
  history,
  ...props
}) => {
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    delete filter[dataIndex];
    history.push(
      QueryString.append(props.location.pathname, { order, ...filter })
    );
  };

  const columns = [
    {
      title: getI18n().t('table_header_id'),
      dataIndex: 'id',
      key: 'id',
      defaultSortOrder:
        order === 'id.asc' ? 'ascend' : order === 'id.desc' ? 'descend' : null,
      sorter: true,
    },
    {
      title: getI18n().t('registrations_username'),
      dataIndex: 'username',
      key: 'username',
      ...getColumnSearchProps(
        'username',
        handleSearch,
        handleReset,
        getI18n().t('registrations_username'),
        filter
      ),
    },
    {
      title: getI18n().t('registrations_email'),
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps(
        'email',
        handleSearch,
        handleReset,
        getI18n().t('registrations_email'),
        filter
      ),
    },
    {
      title: getI18n().t('registrations_first_name'),
      dataIndex: 'first_name',
      key: 'first_name',
    },
    {
      title: getI18n().t('registrations_last_name'),
      dataIndex: 'last_name',
      key: 'last_name',
    },
    {
      title: getI18n().t('registrations_step_1_at'),
      dataIndex: 'step_1_at',
      key: 'step_1_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'step_1_at.asc'
          ? 'ascend'
          : order === 'step_1_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'step_1_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('registrations_step_2_at'),
      dataIndex: 'step_2_at',
      key: 'step_2_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'step_2_at.asc'
          ? 'ascend'
          : order === 'step_2_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'step_2_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('registrations_account_created_at'),
      dataIndex: 'account_created_at',
      key: 'account_created_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'account_created_at.asc'
          ? 'ascend'
          : order === 'account_created_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'account_created_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('registrations_checkout_at'),
      dataIndex: 'checkout_at',
      key: 'checkout_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'checkout_at.asc'
          ? 'ascend'
          : order === 'checkout_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'checkout_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'created_at.asc'
          ? 'ascend'
          : order === 'created_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'created_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_actions'),
      dataIndex: 'id',
      key: 'actions',
      render: (id) => (
        <>
          <Button
            icon={<EyeOutlined />}
            onClick={(e) =>
              history.push(pathTo('RegistrationViewScreen', { id: id }))
            }
          />
        </>
      ),
    },
  ];

  const pagination = {
    current: page,
    pageSize: limit,
    total: total,
    showSizeChanger: false,
    position: ['bottomCenter'],
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let path = props.location.pathname;
    const params = {};

    // handle pagination
    if ('current' in pagination && pagination['current']) {
      path = pathTo(component, { page: pagination['current'] });
    }

    // handle sorting
    if ('field' in sorter && 'order' in sorter) {
      if (sorter['field'] && sorter['order']) {
        const order =
          sorter['field'] +
          '.' +
          (sorter['order'] === 'ascend' ? 'asc' : 'desc');
        params['order'] = order;
      }
    }

    // handle filters
    if (filters) {
      for (const key in filters) {
        if (filters[key]) {
          params[key] = filters[key].join(',');
        }
      }
    }

    history.push(QueryString.append(path, params));
  };

  const filterString = JSON.stringify(filter);
  useEffect(() => {
    load(page, limit, order, JSON.parse(filterString));
  }, [page, limit, order, filterString, load]);

  return (
    <Translation>
      {(t) => (
        <Table
          dataSource={props.list}
          columns={columns}
          pagination={page === 1 && total <= limit ? false : pagination}
          loading={props.isLoading}
          scroll={{ x: true }}
          onChange={handleTableChange}
        />
      )}
    </Translation>
  );
};

export default withRouter(RegistrationsList);

Logger.log('silly', `RegistrationsList loaded.`);
